import {
	createOperationDescriptor,
	getRequest,
	type IEnvironment,
	type GraphQLResponse,
} from 'relay-runtime';

import type { AGGError } from '@atlassian/relay-errors';

import { expVal, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { isMeetingRecorderEligibilityEnabled } from '@confluence/loom-utils/entry-points/isMeetingRecorderEligibilityEnabled.ts';
import { createAtlAttributionHeader } from '@confluence/loom-utils/entry-points/createAtlAttributionHeader.ts';
import { PRELOADED_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/view-page-onboarding-keys.ts';
import {
	ONBOARDING_EXPERIENCES,
	OnboardingKeysCombined,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { ViewPagePersonalizationQuery } from '@confluence/content-unified-query/entry-points/ViewPagePersonalizationQuery.ts';
import type { PersonalizationQuery } from '@confluence/content-unified-query/entry-points/ViewPagePersonalizationQuery.ts';
import {
	buildViewPagePersonalizationVariables,
	type PersonalizationGroups,
} from '@confluence/content-unified-query/entry-points/viewPagePersonalizationVariables.ts';

export type ViewPagePersonalizationResult =
	| {
			status: 'loaded';
			data: PersonalizationQuery['response'];
			variables: PersonalizationQuery['variables'];
			errors: ReadonlyArray<AGGError>;
	  }
	| { status: 'failed' }
	| undefined;

// Preserve fetchQuery's in-flight reuse without sharing errors across different operations or SSR
// environments. Completed requests use Relay's normalized store instead of this map.
const pendingRequests = new WeakMap<
	IEnvironment,
	Map<string, Promise<ViewPagePersonalizationResult>>
>();

export async function preloadViewPagePersonalization({
	cloudId,
	activationId,
	contentId,
	spaceKey,
	isLoggedIn,
	accessStatus,
	isBlog,
	isAdminHubAIEnabled,
	groups,
}: {
	cloudId: string;
	activationId: string;
	contentId: string;
	spaceKey: string;
	isLoggedIn: boolean;
	accessStatus: AccessStatus | null;
	isBlog: boolean;
	isAdminHubAIEnabled: boolean;
	// Route-owned migration readiness, not rollout flags. Only opt in alongside a migrated consumer.
	groups: Partial<Record<keyof PersonalizationGroups, boolean>>;
}): Promise<ViewPagePersonalizationResult> {
	if (!window.GLOBAL_RELAY_ENV) return undefined;

	const included: PersonalizationGroups = {};
	if (
		groups.loom &&
		isLoggedIn &&
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS) &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_loom_reader')
	) {
		included.loom = { includeMeetingRecorderEligibility: false };
	}
	if (
		groups.recommendations &&
		isLoggedIn &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_recs_reader') &&
		!UNSAFE_expValEqualsNoExposure('confluence_eop_recs_twg_source_exp', 'isEnabled', true)
	) {
		included.recommendations = { experience: '' };
	}
	if (
		groups.onboarding &&
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS ||
			accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS) &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_onboarding_reader') &&
		expVal('confluence_pgxp_p2m_batch_1', 'isEnabled', false)
	) {
		included.onboarding = { keys: [] };
	}
	if (
		groups.omnibar &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_onboarding_reader') &&
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		fg('cc-ai-growth-preload-omnibar')
	) {
		included.omnibar = { keys: [ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN] };
	}
	if (
		groups.catchup &&
		isAdminHubAIEnabled &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_catchup_reader')
	) {
		included.catchup = true;
	}
	if (
		groups.presence &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_presence_reader')
	) {
		included.presence = true;
	}
	if (
		!Object.keys(included).length ||
		!expVal('confluence_exp_personalization_contract', 'isEnabled', false)
	) {
		return undefined;
	}

	if (included.loom) {
		included.loom.includeMeetingRecorderEligibility = isMeetingRecorderEligibilityEnabled();
	}
	if (included.recommendations) {
		included.recommendations.experience = expVal(
			'cc_ai_discovery_linked_pages_on_related_pages',
			'experience',
			'end_of_page_v5',
		);
	}
	if (included.onboarding) {
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		included.onboarding.keys = fg('confluence_preload_all_onboarding_keys')
			? OnboardingKeysCombined
			: PRELOADED_KEYS;
	}
	const variables = buildViewPagePersonalizationVariables(
		{ cloudId, contentId, spaceKey, isBlog },
		included,
	);
	const environment = window.GLOBAL_RELAY_ENV;
	const operation = createOperationDescriptor(getRequest(ViewPagePersonalizationQuery), variables, {
		force: true,
		metadata: { headers: createAtlAttributionHeader(cloudId, activationId) },
	});
	let pending = pendingRequests.get(environment);
	if (!pending) {
		pending = new Map();
		pendingRequests.set(environment, pending);
	}
	const existing = pending.get(operation.request.identifier);
	if (existing) return existing;
	const request = (async (): Promise<ViewPagePersonalizationResult> => {
		// Like fetchQuery, this imperative preload does not own a retain. Releasing one here
		// can overflow Relay's release buffer and collect other preloads before hydration.
		// Mounted query readers own retention for the data they consume.
		try {
			const errors: AGGError[] = [];
			if (environment.check(operation).status !== 'available') {
				// Observe this execution's payload, not the global error collector: other groups use
				// the same operation name concurrently with different variables.
				await environment
					.execute({ operation })
					.map((response) => {
						errors.push(...getResponseErrors(response));
						return response;
					})
					.toPromise();
			}
			const data = environment.lookup(operation.fragment).data as PersonalizationQuery['response'];
			if (!data || errors.some(({ path }) => !path?.length)) return { status: 'failed' };
			return { status: 'loaded', data, variables, errors };
		} catch {
			return { status: 'failed' };
		}
	})();
	pending.set(operation.request.identifier, request);
	try {
		return await request;
	} finally {
		pending.delete(operation.request.identifier);
	}
}

/** A healthy, included group may replace its legacy preloader; successful null is data. */
export function hasPersonalizationGroup(
	result: ViewPagePersonalizationResult,
	include: keyof PersonalizationQuery['variables'],
	fields: ReadonlyArray<keyof PersonalizationQuery['response']>,
): boolean {
	if (
		result?.status !== 'loaded' ||
		result.variables[include] !== true ||
		!fields.every((field) => result.data[field] !== undefined) ||
		result.errors.some(
			({ path }) =>
				!path?.length || fields.includes(path[0] as keyof PersonalizationQuery['response']),
		)
	)
		return false;
	const variables = {
		...result.variables,
		includeLoom: false,
		includeRecommendations: false,
		includeOnboardingState: false,
		includeAIFloatingContextMenu: false,
		includePresenceSettings: false,
		[include]: true,
	};
	const operation = createOperationDescriptor(getRequest(ViewPagePersonalizationQuery), variables);
	return window.GLOBAL_RELAY_ENV?.check(operation).status === 'available';
}

function getResponseErrors(response: GraphQLResponse): AGGError[] {
	const payloads = Array.isArray(response) ? response : [response];
	return payloads.flatMap((payload) => ('errors' in payload ? (payload.errors ?? []) : []));
}
